import { Component, inject, signal } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApprovalService } from '../core/services/approval.service';
import { AuthService } from '../core/services/auth.service';
import { AuditEvent, TransactionDetail as TransactionDetailModel } from '../core/models/transaction.model';
import { StatusBadge } from '../shared/components/status-badge';
import { ConfirmDialog } from '../shared/components/confirm-dialog';
import { ApprovalApiError } from '../core/errors/approval-api-error';

@Component({
  selector: 'app-transaction-detail',
  imports: [
    RouterLink,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    StatusBadge,
    DatePipe,
    CurrencyPipe,
  ],
  templateUrl: './transaction-detail.html',
  styleUrl: './transaction-detail.scss',
})
export class TransactionDetail {
  private readonly route = inject(ActivatedRoute);
  private readonly approvalService = inject(ApprovalService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  private readonly controlReference =
    this.route.snapshot.paramMap.get('controlReference') ?? '';

  readonly transaction = signal<TransactionDetailModel | null>(null);
  readonly audit = signal<AuditEvent[]>([]);
  readonly loading = signal(false);
  readonly deciding = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly decisionBanner = signal<string | null>(null);
  readonly rejectionReason = signal('');

  readonly canDecide = () => this.authService.role() !== 'AUDITOR';

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.approvalService.getTransaction(this.controlReference).subscribe({
      next: (transaction) => {
        this.loading.set(false);
        this.transaction.set(transaction);
      },
      error: (err: unknown) => {
        this.loading.set(false);
        this.errorMessage.set(
          err instanceof ApprovalApiError ? err.message : 'Could not load transaction.',
        );
      },
    });

    this.approvalService.getAuditHistory(this.controlReference).subscribe({
      next: (events) => this.audit.set(events),
      error: () => this.audit.set([]),
    });
  }

  isPending(): boolean {
    return this.transaction()?.state === 'PENDING_APPROVAL';
  }

  approve(): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Approve transaction',
        message: `Approve control reference ${this.controlReference}? This action is final.`,
        confirmLabel: 'Approve',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.deciding.set(true);
      this.decisionBanner.set(null);

      this.approvalService.approve(this.controlReference).subscribe({
        next: () => {
          this.deciding.set(false);
          this.snackBar.open('Transaction approved.', 'Dismiss', { duration: 4000 });
          this.refresh();
        },
        error: (err: unknown) => {
          this.deciding.set(false);
          const message =
            err instanceof ApprovalApiError ? err.message : 'Approval failed.';
          this.decisionBanner.set(
            `${message} Rejection is still available to release funds.`,
          );
          this.refresh();
        },
      });
    });
  }

  reject(): void {
    if (!this.rejectionReason().trim()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Reject transaction',
        message: `Reject control reference ${this.controlReference}? This action is final and releases the wallet lien.`,
        confirmLabel: 'Reject',
        danger: true,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.deciding.set(true);
      this.decisionBanner.set(null);

      this.approvalService.reject(this.controlReference, this.rejectionReason().trim()).subscribe({
        next: () => {
          this.deciding.set(false);
          this.snackBar.open('Transaction rejected.', 'Dismiss', { duration: 4000 });
          this.refresh();
        },
        error: (err: unknown) => {
          this.deciding.set(false);
          this.decisionBanner.set(
            err instanceof ApprovalApiError ? err.message : 'Rejection failed.',
          );
        },
      });
    });
  }
}
